declare const posthog: any;

import Swiper from 'swiper';
import { Navigation, Thumbs, Zoom, Keyboard } from 'swiper/modules';
import { getCreditUnionGroups } from './posthog-group';

/**
 * Focus One vehicle gallery + full-screen photo viewer.
 *
 * Runs only when the Focus One markup is on the page, so the legacy details page keeps using
 * handle-detail-swipers.ts untouched. Uses Swiper's own Zoom module (already part of the swiper
 * package) for double-click / pinch zoom and drag-to-pan — no extra dependency.
 *
 * The module's CSS lives in src/css/focusone-gallery.css, not an import from swiper/css/zoom:
 * Vite puts node_modules CSS in vendor.css, which no layout links, so a package CSS import
 * silently never reaches the browser. overwrites.css already handles the base swiper styles
 * the same way.
 */
const galleryEl = document.querySelector<HTMLElement>('[data-vehicle-gallery]');
const lightboxEl = document.querySelector<HTMLElement>('[data-vehicle-lightbox]');

if (galleryEl && lightboxEl) {
    const vehicleId = galleryEl.getAttribute('data-vehicle-id') ?? undefined;
    const counterEl = lightboxEl.querySelector<HTMLElement>('[data-lightbox-counter]');
    const totalSlides = lightboxEl.querySelectorAll('.fo-lightbox-main .swiper-slide').length;

    const capturePhotoEvent = (event: string, index: number) => {
        if (typeof posthog === 'undefined') return;
        posthog.capture(event, {
            vehicle_id: vehicleId,
            photo_index: index,
            total_photos: totalSlides,
            $groups: getCreditUnionGroups(),
        });
    };

    // ---------- Inline gallery ----------
    const inlineThumbs = new Swiper('.fo-gallery-thumbs', {
        modules: [Navigation, Thumbs],
        spaceBetween: 8,
        // Five across at every width: the design shows five on mobile, and the "+N" badge the
        // markup puts on the fifth thumbnail only reads correctly when five are visible.
        slidesPerView: 5,
        watchSlidesProgress: true,
    });

    const inlineMain = new Swiper('.fo-gallery-main', {
        modules: [Navigation, Thumbs],
        spaceBetween: 10,
        navigation: { nextEl: '.fo-gallery-next', prevEl: '.fo-gallery-prev' },
        thumbs: { swiper: inlineThumbs },
        on: {
            // Same event the legacy gallery reports, so reporting is unchanged by the redesign.
            slideChange: (swiper: any) => capturePhotoEvent('vehicle_photo_swiped', swiper.realIndex),
        },
    });

    // ---------- Full-screen viewer ----------
    let lightboxMain: any = null;
    let lightboxThumbs: any = null;
    let lastFocused: HTMLElement | null = null;
    let lockedScrollY = 0;

    const isOpen = () => lightboxEl.classList.contains('is-open');

    /**
     * iOS Safari ignores `overflow: hidden` on body, so the page keeps scrolling behind the
     * viewer. Pinning the body and restoring the offset on close is what actually holds.
     */
    const lockScroll = () => {
        lockedScrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${lockedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
    };

    const unlockScroll = () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo(0, lockedScrollY);
    };

    const setCounter = (index: number) => {
        if (counterEl) counterEl.textContent = `${index + 1} / ${totalSlides}`;
    };

    /** Swaps data-src to src the first time the viewer opens, so full-size photos aren't fetched up front. */
    const loadLightboxImages = () => {
        lightboxEl.querySelectorAll<HTMLImageElement>('img[data-src]').forEach((img) => {
            img.src = img.getAttribute('data-src') ?? '';
            img.removeAttribute('data-src');
        });
    };

    const buildLightbox = () => {
        if (lightboxMain) return;

        lightboxThumbs = new Swiper('.fo-lightbox-thumbs', {
            modules: [Thumbs],
            spaceBetween: 8,
            slidesPerView: 5,
            watchSlidesProgress: true,
            breakpoints: { 768: { slidesPerView: 8 } },
        });

        lightboxMain = new Swiper('.fo-lightbox-main', {
            modules: [Navigation, Thumbs, Zoom, Keyboard],
            spaceBetween: 16,
            zoom: { maxRatio: 4, toggle: true },
            keyboard: { enabled: true },
            navigation: { nextEl: '.fo-lightbox-next', prevEl: '.fo-lightbox-prev' },
            thumbs: { swiper: lightboxThumbs },
            on: {
                slideChange: (swiper: any) => {
                    setCounter(swiper.realIndex);
                    // Leaving a zoomed slide zoomed makes the next one look broken.
                    swiper.zoom?.out();
                    capturePhotoEvent('vehicle_photo_swiped', swiper.realIndex);
                },
                zoomChange: (_s: any, scale: number) => {
                    if (scale > 1) capturePhotoEvent('vehicle_photo_zoomed', lightboxMain?.realIndex ?? 0);
                },
            },
        });
    };

    const openLightbox = (index: number) => {
        lastFocused = document.activeElement as HTMLElement;
        loadLightboxImages();
        lightboxEl.classList.add('is-open');
        // Scroll lock is scoped to the viewer rather than added to the shared modal script.
        lockScroll();

        buildLightbox();
        lightboxMain.update();
        lightboxThumbs.update();
        lightboxMain.slideTo(index, 0);
        setCounter(index);

        lightboxEl.querySelector<HTMLElement>('[data-lightbox-close]')?.focus();
        capturePhotoEvent('vehicle_gallery_opened', index);
    };

    const closeLightbox = () => {
        if (isOpen() === false) return;
        lightboxMain?.zoom?.out();
        lightboxEl.classList.remove('is-open');
        unlockScroll();
        // Keep the inline gallery on whatever photo the viewer ended on.
        if (lightboxMain) inlineMain.slideTo(lightboxMain.realIndex, 0);
        lastFocused?.focus();
    };

    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;

        const opener = target.closest<HTMLElement>('[data-gallery-open]');
        if (opener) {
            event.preventDefault();
            const explicit = opener.getAttribute('data-gallery-index');
            openLightbox(explicit !== null ? Number(explicit) : inlineMain.realIndex);
            return;
        }

        if (target.closest('[data-lightbox-close]')) {
            event.preventDefault();
            closeLightbox();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (isOpen() === false) return;
        if (event.key === 'Escape') closeLightbox();
    });

    // Swipe down to dismiss. Deliberately narrow: only when the photo is not zoomed (otherwise this
    // would fight Swiper's pan) and only for a clearly vertical drag, so horizontal slide gestures
    // are untouched. Listeners are passive — they never block Swiper's own handling.
    let touchStartX = 0;
    let touchStartY = 0;
    let touchTracking = false;

    lightboxEl.addEventListener('touchstart', (event) => {
        touchTracking = isOpen() && event.touches.length === 1 && (lightboxMain?.zoom?.scale ?? 1) <= 1;
        if (touchTracking) {
            touchStartX = event.touches[0].clientX;
            touchStartY = event.touches[0].clientY;
        }
    }, { passive: true });

    lightboxEl.addEventListener('touchend', (event) => {
        if (touchTracking === false) return;
        touchTracking = false;

        const touch = event.changedTouches[0];
        if (!touch) return;

        const dx = touch.clientX - touchStartX;
        const dy = touch.clientY - touchStartY;

        if (dy > 90 && Math.abs(dy) > Math.abs(dx) * 2) closeLightbox();
    }, { passive: true });
}
